import { createSelector } from '@reduxjs/toolkit'
import { convertPricePeriods, getPeriodText } from 'functions/functions'
import {
  getAllAddons,
  selectBasketPageOptions,
  selectStoreItems,
  selectPCWData,
} from '../dataStore/dataStore.selectors'
import { selectLogicStore } from '../root.selectors'

export const getFirstBasketItem = createSelector(
  selectLogicStore,
  selectStoreItems,
  (logic, storeItems) => {
    if (logic.basket.length === 0) {
      throw new Error('no items in basket')
    }
    const id = logic.basket[0]

    const item = storeItems.find((cardItem) => cardItem.id === id)

    if (item === undefined) {
      throw new Error(`item in basket "${id}" not found in dataStore`)
    }
    return item
  }
)

export const getAddonsInBasket = createSelector(
  selectLogicStore,
  (logic) => logic.basketAddons
)

export const getPCWProductInBasket = createSelector(
  selectLogicStore,
  selectPCWData,
  (logic, tableItems) => {
    const id = logic.pcwPickedProduct[0]
    const product = tableItems.find((row) => row.id === id)
    if (product === undefined) {
      throw new Error(`item in basket "${id} " not found in dataStore`)
    }
    return product
  }
)

export const getAddonsForSelectedItem = createSelector(
  getAllAddons,
  getFirstBasketItem,
  (addons, item) =>
    addons.filter(
      (addon) =>
        (addon.includedProductIDs === undefined ||
          addon.includedProductIDs.some(
            (productID) => productID === item.id
          )) &&
        (addon.excludedProductIDs === undefined ||
          addon.excludedProductIDs.every((productID) => productID !== item.id))
    )
)

/**
 * Check to see if the basket contains any mutex addons
 * @returns boolean Whether there are any mutex addons in the basket
 */
export const selectAnyMutexAddonsInBasket = createSelector(
  getAddonsInBasket,
  (addons) => addons.some((addon) => addon.mutex)
)

export const getCostBreakdown = createSelector(
  getFirstBasketItem,
  getAddonsInBasket,
  selectBasketPageOptions,
  (item, selectedAddons, basketPageOptions) => {
    // sum up the total price of selected addons and card
    let totalBasketPrice = 0

    // sum up total addons price
    if (selectedAddons !== undefined && selectedAddons.length !== 0) {
      totalBasketPrice += selectedAddons.reduce(
        (sum, basketAddon) =>
          sum +
          convertPricePeriods(
            basketAddon.price || 0,
            basketAddon.pricePeriod,
            basketPageOptions?.pagePricePeriod
          ),
        0
      )
    }

    // add card price to totalBasketPrice
    if (item !== undefined && item.price) {
      let itemPrice = item.price
      itemPrice = convertPricePeriods(
        itemPrice,
        item.pricePeriod,
        basketPageOptions?.pagePricePeriod
      )
      totalBasketPrice += itemPrice
    }

    // When user doesn't select addons we display card priceString in the basket
    if (
      (selectedAddons === undefined || selectedAddons.length === 0) &&
      item?.priceString !== undefined
    ) {
      return item.priceString
    }

    // handle currency conversion
    const totalPriceString = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: item?.currency || 'GBP',
    }).format(totalBasketPrice)

    // add the price period if needed
    let periodString = ''
    if (basketPageOptions?.pagePricePeriod) {
      periodString = ` ${getPeriodText(basketPageOptions?.pagePricePeriod)}`
    } else if (item.pricePeriod) {
      periodString = ` ${getPeriodText(item.pricePeriod)}`
    }

    return `${totalPriceString}${periodString}`
  }
)

export const getMoreInfoClicks = createSelector(
  selectLogicStore,
  (store) => store.pcwMoreInfoClicked
)

export const getSortByHistory = createSelector(
  selectLogicStore,
  (store) => store.sortedByHistory
)
